import React, { useEffect, useState } from 'react';
import { TrendingUp, Building2, Shield, AlertTriangle } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { cbuApi } from '../../../services/api';
import type { BankingSectorData } from '../../../services/api/cbuApi';

interface BankingSectorPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const BankingSectorPanel: React.FC<BankingSectorPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<BankingSectorData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await cbuApi.getBankingSectorData();
      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const metrics = data ? [
    {
      icon: Building2,
      label: 'Total Assets',
      value: `$${data.totalAssets}B`,
      description: 'Banking sector assets',
      color: 'text-data-blue',
    },
    {
      icon: Shield,
      label: 'Capital Adequacy',
      value: `${data.capitalAdequacy}%`,
      description: 'CAR (Basel III)',
      color: 'text-data-green',
      threshold: { min: 10.5, current: data.capitalAdequacy },
    },
    {
      icon: AlertTriangle,
      label: 'NPL Ratio',
      value: `${data.nplRatio}%`,
      description: 'Non-performing loans',
      color: data.nplRatio < 5 ? 'text-data-green' : 'text-data-amber',
      threshold: { max: 5, current: data.nplRatio },
    },
    {
      icon: TrendingUp,
      label: 'Credit Growth',
      value: `+${data.creditGrowth}%`,
      description: 'YoY loan growth',
      color: 'text-data-amber',
    },
  ] : [];

  const exportData = metrics.map(m => ({
    indicator: m.label,
    value: m.value,
    description: m.description,
    source: data?.source || 'CBU',
  }));

  return (
    <Panel
      id="banking-sector"
      title="Banking Sector"
      subtitle="UZB"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col">
        {/* Source Badge */}
        <div className="flex items-center gap-2 mb-2 px-1">
          <DataSourceBadge source={DATA_SOURCES.CBU} showLabel size="xs" />
          {data?.lastUpdated && (
            <span className="text-xs text-gray-500">
              {data.lastUpdated}
            </span>
          )}
        </div>

        {/* Metrics Grid */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">
            Loading banking data...
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-2 gap-2">
            {metrics.map(metric => (
              <div
                key={metric.label}
                className="p-2 bg-terminal-border/30 rounded flex flex-col"
              >
                <div className="flex items-center gap-1 mb-1">
                  <metric.icon size={12} className={metric.color} />
                  <span className="text-xs text-gray-500">{metric.label}</span>
                </div>
                <div className={`text-lg font-mono font-bold ${metric.color}`}>
                  {metric.value}
                </div>
                <div className="text-xs text-gray-600 mt-auto">
                  {metric.description}
                </div>

                {/* Threshold indicator */}
                {metric.threshold && (
                  <div className="mt-1">
                    <div className="h-1 bg-terminal-border rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          metric.threshold.min
                            ? metric.threshold.current >= metric.threshold.min
                              ? 'bg-data-green'
                              : 'bg-data-red'
                            : metric.threshold.current <= (metric.threshold.max || 0)
                              ? 'bg-data-green'
                              : 'bg-data-amber'
                        }`}
                        style={{
                          width: `${Math.min(
                            metric.threshold.min
                              ? (metric.threshold.current / 25) * 100
                              : (metric.threshold.current / 10) * 100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {metric.threshold.min
                        ? `Min: ${metric.threshold.min}%`
                        : `Target: <${metric.threshold.max}%`}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Growth Comparison */}
        {data && (
          <div className="mt-2 pt-2 border-t border-terminal-border/50">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <div className="text-xs text-gray-500">Credit Growth</div>
                <div className="text-sm font-mono text-data-green">+{data.creditGrowth}%</div>
              </div>
              <div className="w-px h-6 bg-terminal-border" />
              <div className="text-center flex-1">
                <div className="text-xs text-gray-500">Deposit Growth</div>
                <div className="text-sm font-mono text-data-blue">+{data.depositGrowth}%</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-2 pt-1 border-t border-terminal-border/50 text-xs text-gray-600">
          Source: {data?.source || 'Central Bank of Uzbekistan'}
        </div>
      </div>
    </Panel>
  );
};

export default BankingSectorPanel;
